import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { accountsApi, Account } from '../api/accounts';
import { leadsApi } from '../api/leads';
import { tagsApi } from '../api/tags';
import PremiumLayout from '../components/PremiumLayout';
import TagSelector from '../components/TagSelector';
import TagDisplay from '../components/TagDisplay';
import CustomFieldsRenderer from '../components/CustomFieldsRenderer';
import { customFieldsApi } from '../api/customFields';
import { PlusIcon, PencilIcon, TrashIcon, BuildingOfficeIcon, GlobeAltIcon, PhoneIcon, ChartBarIcon, EyeIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const Accounts: React.FC = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    website: '',
    phone: '',
    address: '',
  });
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});

  const queryClient = useQueryClient();

  const { data: accounts = [], isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: accountsApi.getAll,
  });

  const { data: leads = [] } = useQuery({
    queryKey: ['leads'],
    queryFn: leadsApi.getAll,
  });

  // Get lead count for each account
  const getLeadCount = (accountId: number) => leads.filter(l => l.account_id === accountId).length;

  // Filter accounts based on search term
  const filteredAccounts = accounts.filter((account) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const name = (account.name || '').toLowerCase();
    const industry = (account.industry || '').toLowerCase();
    const website = (account.website || '').toLowerCase();
    const phone = (account.phone || '').toLowerCase();
    const address = (account.address || '').toLowerCase();
    
    return name.includes(searchLower) || 
           industry.includes(searchLower) || 
           website.includes(searchLower) ||
           phone.includes(searchLower) ||
           address.includes(searchLower);
  });

  const createMutation = useMutation({
    mutationFn: accountsApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      setShowModal(false);
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Account> }) =>
      accountsApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });
      setShowModal(false);
      setEditingAccount(null);
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: accountsApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      industry: '',
      website: '',
      phone: '',
      address: '',
    });
    setSelectedTagIds([]);
    setCustomFieldValues({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingAccount) {
      updateMutation.mutate(
        { id: editingAccount.id, data: formData },
        {
          onSuccess: async () => {
            try {
              await tagsApi.setTags('account', editingAccount.id, selectedTagIds);
              queryClient.invalidateQueries({ queryKey: ['tags', 'account', editingAccount.id] });
            } catch (error) {
              console.error('Error updating tags:', error);
            }
            try {
              if (Object.keys(customFieldValues).length > 0) {
                await customFieldsApi.setValues('account', editingAccount.id, customFieldValues);
              }
            } catch (error) {
              console.error('Error updating custom fields:', error);
            }
          },
        }
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: async (newAccount) => {
          if (selectedTagIds.length > 0) {
            try {
              await tagsApi.setTags('account', newAccount.id, selectedTagIds);
              queryClient.invalidateQueries({ queryKey: ['tags', 'account', newAccount.id] });
            } catch (error) {
              console.error('Error setting tags:', error);
            }
          }
          try {
            if (Object.keys(customFieldValues).length > 0) {
              await customFieldsApi.setValues('account', newAccount.id, customFieldValues);
            }
          } catch (error) {
            console.error('Error setting custom fields:', error);
          }
        },
      });
    }
  };

  const handleEdit = async (account: Account) => {
    setEditingAccount(account);
    setFormData({
      name: account.name,
      industry: account.industry || '',
      website: account.website || '',
      phone: account.phone || '',
      address: account.address || '',
    });
    // Load tags for this account
    try {
      const tags = await tagsApi.getByEntity('account', account.id);
      setSelectedTagIds(tags.map(t => t.id));
    } catch (error) {
      console.error('Error loading tags:', error);
      setSelectedTagIds([]);
    }
    // Load custom field values
    try {
      const customValues = await customFieldsApi.getValues('account', account.id);
      const values: Record<string, any> = {};
      Object.keys(customValues).forEach(key => {
        values[key] = customValues[key]?.value || customValues[key];
      });
      setCustomFieldValues(values);
    } catch (error) {
      console.error('Error loading custom fields:', error);
      setCustomFieldValues({});
    }
    setShowModal(true);
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Are you sure you want to delete this account? This action cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <PremiumLayout title="Accounts">
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-indigo-200 border-t-indigo-600"></div>
        </div>
      </PremiumLayout>
    );
  }

  return (
    <PremiumLayout
      title="Accounts"
      subtitle={`${filteredAccounts.length} of ${accounts.length} companies`}
      actions={
        <button
          onClick={() => {
            resetForm();
            setEditingAccount(null);
            setShowModal(true);
          }}
          className="btn-primary text-sm px-4 py-2"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          New Account
        </button>
      }
    >
      <div className="space-y-6">
        {/* Search Bar */}
        <div className="premium-card p-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search by company name, industry, website, phone, or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-premium pl-10"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAccounts.map((account) => {
            const leadCount = getLeadCount(account.id);
            return (
              <div
                key={account.id}
                className="premium-card-hover p-6 group cursor-pointer"
                onClick={() => navigate(`/accounts/${account.id}`)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4 flex-1 min-w-0">
                    <div className="w-14 h-14 bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                      <BuildingOfficeIcon className="h-7 w-7 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900 truncate">{account.name}</h3>
                      {account.industry && (
                        <p className="text-sm text-gray-500 mt-0.5">{account.industry}</p>
                      )}
                      <TagDisplay entityType="account" entityId={account.id} className="mt-2" />
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/accounts/${account.id}`); }}
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="View Details"
                    >
                      <EyeIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(account); }}
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(account.id); }}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                {/* Lead count badge */}
                {leadCount > 0 && (
                  <div className="flex items-center mb-3 px-3 py-1.5 bg-blue-50 rounded-lg">
                    <ChartBarIcon className="h-4 w-4 text-blue-500 mr-2" />
                    <span className="text-sm font-medium text-blue-700">{leadCount} Lead{leadCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
                <div className="space-y-2 pt-4 border-t border-gray-100">
                  {account.website && (
                    <div className="flex items-center text-sm text-gray-600">
                      <GlobeAltIcon className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                      <a
                        href={account.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-indigo-600 truncate"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {account.website}
                      </a>
                    </div>
                  )}
                  {account.phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <PhoneIcon className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                      <a href={`tel:${account.phone}`} className="hover:text-indigo-600" onClick={(e) => e.stopPropagation()}>
                        {account.phone}
                      </a>
                    </div>
                  )}
                  {account.address && (
                    <div className="flex items-start text-sm text-gray-600">
                      <BuildingOfficeIcon className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0 mt-0.5" />
                      <span className="line-clamp-2">{account.address}</span>
                    </div>
                  )}
                </div>
                {/* Click hint */}
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-center text-gray-400 group-hover:text-indigo-500">
                  Click to view all leads & details →
                </div>
              </div>
            );
          })}
        </div>

        {filteredAccounts.length === 0 && accounts.length > 0 && (
          <div className="premium-card p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <MagnifyingGlassIcon className="h-10 w-10 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Accounts Found</h3>
              <p className="text-gray-500 mb-6">
                No accounts match your search term "{searchTerm}". Try a different search.
              </p>
              <button
                onClick={() => setSearchTerm('')}
                className="btn-secondary"
              >
                Clear Search
              </button>
            </div>
          </div>
        )}

        {accounts.length === 0 && (
          <div className="premium-card p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <BuildingOfficeIcon className="h-10 w-10 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">No Accounts Yet</h3>
              <p className="text-gray-500 mb-6">
                Start building your account database by adding your first company account.
              </p>
              <button
                onClick={() => {
                  resetForm();
                  setEditingAccount(null);
                  setShowModal(true);
                }}
                className="btn-primary"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Your First Account
              </button>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-start justify-center pt-4 pb-4 px-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full transform transition-all fade-in">
              <div className="p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {editingAccount ? 'Edit Account' : 'New Account'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {editingAccount ? 'Update account information' : 'Add a new company account to your database'}
                  </p>
                </div>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Company Name (Brand) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-premium"
                      placeholder="Enter company name"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Industry
                      </label>
                      <input
                        type="text"
                        value={formData.industry}
                        onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                        className="input-premium"
                        placeholder="e.g., Technology"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="input-premium"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Website
                    </label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      className="input-premium"
                      placeholder="https://example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Address
                    </label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      rows={3}
                      className="input-premium"
                      placeholder="Enter company address"
                    />
                  </div>
                  <TagSelector
                    selectedTagIds={selectedTagIds}
                    onChange={setSelectedTagIds}
                    entityType="account"
                    entityId={editingAccount?.id}
                  />
                  <CustomFieldsRenderer
                    entityType="account"
                    entityId={editingAccount?.id}
                    values={customFieldValues}
                    onChange={(fieldName, value) => {
                      setCustomFieldValues(prev => ({
                        ...prev,
                        [fieldName]: value,
                      }));
                    }}
                  />
                  <div className="flex space-x-3 pt-6 border-t border-gray-200 mt-6">
                    <button
                      type="submit"
                      disabled={createMutation.isPending || updateMutation.isPending}
                      className="btn-primary flex-1"
                    >
                      {createMutation.isPending || updateMutation.isPending
                        ? 'Saving...'
                        : editingAccount
                        ? 'Update Account'
                        : 'Create Account'}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowModal(false);
                        setEditingAccount(null);
                        resetForm();
                      }}
                      className="btn-secondary flex-1"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </PremiumLayout>
  );
};

export default Accounts;
